import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import { CalendarConnectionConfigurationListView } from "@/components/calendar/calendar-connection-configuration-list";
import {
  StepTitle,
  StepContent,
  FooterContainer,
  FooterButton,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { OnboardingStepper } from "@/components/onboarding/components/welcome-wizard/shared";
import { MDSText, MDSTextVariant } from "@/mds";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent, CalendarConnectErrorType } from "@/domains/metrics/tracked-event";
import { getCalendarConnectSource } from "@/domains/calendar/getCalendarConnectSource";
import { windowModule } from "@/modules/window";
import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export const CalendarSelectStep: FC<StepRenderProps> = observer(({ onComplete }) => {
  const { store } = useAppStore();

  const pendingConnection = store.calendarOnboarding.pendingCalendarConnection;
  const showFinish = store.onboarding.showFinish;
  const provider = pendingConnection?.provider;

  const googleStore = store.calendar.google;
  const outlookStore = store.calendar.outlook;

  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [togglingCalendar, setTogglingCalendar] = useState<{
    id: string;
    targetEnabled: boolean;
  } | null>(null);
  const connectCalledRef = useRef(false);

  // On mount, call completeConnection with the OAuth params
  useEffect(() => {
    if (!pendingConnection) return;
    if (connectCalledRef.current) return;
    connectCalledRef.current = true;

    const { code, state, provider: connProvider } = pendingConnection;
    const connect = async () => {
      const redirectUri =
        connProvider === "google"
          ? windowModule.getGoogleCalendarOAuthRedirectUrl()
          : windowModule.getOutlookCalendarOAuthRedirectUrl();

      const calendarStore = connProvider === "google" ? googleStore : outlookStore;

      try {
        const result = await calendarStore.completeConnection({ code, state, redirectUri });
        setSubjectId(result.subjectId);

        trackEvent(TrackedEvent.CalendarConnectComplete, {
          provider: connProvider,
          calendar_connection_id: result.subjectId,
          source: getCalendarConnectSource(),
        });
      } catch (_unknownError) {
        const error = _unknownError as Error;
        const errorType: CalendarConnectErrorType = error.message?.includes("state mismatch")
          ? "state_mismatch"
          : "api_error";

        trackEvent(TrackedEvent.CalendarConnectFailed, {
          provider: connProvider,
          error_type: errorType,
          error_message: error.message,
          source: getCalendarConnectSource(),
        });

        logger.error({
          message: "[CalendarSelectStep] Failed to complete calendar connection",
          info: { provider: connProvider, error: objectModule.safeErrorAsJson(error) },
        });

        toastModule.triggerToast({
          content: `Unable to complete ${connProvider === "google" ? "Google" : "Outlook"} Calendar connection. Please try again.`,
        });

        setHasError(true);
      } finally {
        setIsConnecting(false);
      }
    };

    void connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If connection failed, reset to not-started so declarative routing shows calendar-connect
  useEffect(() => {
    if (hasError) {
      store.calendarOnboarding.reset();
    }
  }, [hasError, store]);

  const googleAccount =
    provider === "google" && subjectId ? googleStore.accounts.get(subjectId) : undefined;
  const outlookAccount =
    provider === "outlook" && subjectId ? outlookStore.accounts.get(subjectId) : undefined;
  const account = googleAccount ?? outlookAccount;

  const isUpdatingCalendars =
    provider === "google" ? googleStore.isUpdatingCalendars : outlookStore.isUpdatingCalendars;

  useEffect(() => {
    if (!isUpdatingCalendars) setTogglingCalendar(null);
  }, [isUpdatingCalendars]);

  const handleToggle = useCallback(
    (calendarId: string, isEnabled: boolean) => {
      if (!subjectId || !provider) return;
      setTogglingCalendar({ id: calendarId, targetEnabled: isEnabled });
      if (provider === "google") {
        void googleStore.toggleCalendar({ subjectId, calendarId, isEnabled });
      } else {
        void outlookStore.toggleCalendar({ subjectId, calendarId, isEnabled });
      }
    },
    [provider, subjectId, googleStore, outlookStore]
  );

  const calendars = useMemo(() => {
    if (!account) return [];
    return account.calendars.map(calendar => {
      const isTogglingThis = togglingCalendar?.id === calendar.id;
      const togglingStatus = isTogglingThis
        ? togglingCalendar.targetEnabled
          ? ("CONNECTING" as const)
          : ("DISCONNECTING" as const)
        : null;
      const isCalendarEnabled = isTogglingThis
        ? togglingCalendar.targetEnabled
        : calendar.isEnabled;

      return {
        id: calendar.id,
        label: calendar.summary ?? calendar.id,
        isPrimary: calendar.isPrimary,
        isCalendarEnabled,
        isDisabled: isUpdatingCalendars,
        togglingStatus,
        backgroundColor: calendar.backgroundColor,
        webhookStatus: null,
        syncStartedAt: null,
        syncEndedAt: null,
        lastSyncEndedWithError: null,
        onToggle: (nextEnabled: boolean) => handleToggle(calendar.id, nextEnabled),
      };
    });
  }, [account, isUpdatingCalendars, togglingCalendar, handleToggle]);

  const email = useMemo(() => {
    if (googleAccount) {
      return googleAccount.googleAccountEmail ?? null;
    }
    if (outlookAccount) {
      return outlookAccount.outlookAccountEmail ?? null;
    }
    return null;
  }, [googleAccount, outlookAccount]);

  const handleNext = useCallback(() => {
    trackEvent(TrackedEvent.OnboardingCalendarSetupComplete, {
      provider,
      calendar_count: calendars.filter(c => c.isCalendarEnabled).length,
    });

    // Let the OnboardingStore pipeline advance to the next step
    onComplete();
  }, [onComplete, provider, calendars]);

  // No pending connection — declarative routing will show calendar-connect
  if (!pendingConnection) {
    return null;
  }

  return (
    <>
      <StepTitle>Choose calendars to see in Mem</StepTitle>
      <StepContent overflowY="auto">
        <MDSText variant={MDSTextVariant.ParagraphSm}>
          Select calendars to view in Mem and use for meeting notifications
        </MDSText>

        <StyledCalendarList>
          <CalendarConnectionConfigurationListView
            email={email}
            provider={provider ?? "google"}
            calendars={calendars}
            connectionStatus={isConnecting ? "LOADING" : "CONNECTED"}
          />
        </StyledCalendarList>
      </StepContent>

      <FooterContainer align="flex-end">
        <OnboardingStepper />
        <FooterButton onClick={handleNext} isDisabled={isConnecting}>
          {showFinish ? "Finish" : "Next"}
        </FooterButton>
      </FooterContainer>
    </>
  );
});

const StyledCalendarList = styled.div`
  flex: 1;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
`;
